/**
 * Zunelo Central State Context
 * Orchestrates domain data, client communication, optimistic mutations,
 * async loading/error states, cross-references, and modal navigation.
 * Composes domain-specific slices for Jira, GitHub, Slack, Agents, Tools, and UI.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import type {
  AttentionItem,
  AgentItem,
  SourceType,
  HoldInfo,
  StateSuggestion,
  SentryOrExtSignal,
  CurrentUserIdentity,
} from '../domain/types';

import { sourceLoadErrorMessage } from '../domain/sourceErrors';
import { getGitHubItemsByTicket as getGitHubItemsByTicketHelper } from '../domain/crossCards';
import { attentionSiblingKeys, buildSiblingKeysMap } from '../domain/projectCardIdentity';
import {
  removeRadarAiAttachment,
  toRadarAiAttachment,
  upsertRadarAiAttachment,
  indexAttentionItemWorkContexts,
  type AttentionItemWorkContextMembership,
  type RadarAiAttachment,
  type WorkThread,
} from '../domain/radarThreads';
import { reloadAttentionSource } from './retrySourceLoad';
import { useSourceSyncIndex } from './useSourceSyncIndex';
import { runIncrementalBootstrap } from './loadAllDataSWR';
import { MetricsService } from '../services/metricsService';
import { toErrorMessage } from '../services/errors';

import { useUiNavigationSlice } from './slices/useUiNavigationSlice';
import { useJiraSlice } from './slices/useJiraSlice';
import { useGitHubSlice } from './slices/useGitHubSlice';
import { useSlackSlice } from './slices/useSlackSlice';
import { useAgentsSlice } from './slices/useAgentsSlice';
import { useWorkspaceToolsSlice } from './slices/useWorkspaceToolsSlice';
import { useConversationsSlice } from './slices/useConversationsSlice';
import { useUnattendedInboxSlice } from './slices/useUnattendedInboxSlice';
import { useNotificationsSlice } from './slices/useNotificationsSlice';

type PrTalkMap = Record<string, Array<{ ch: string; text: string; when: string; id: string }>>;

/**
 * The context type is derived from each slice hook's own return type instead
 * of being hand-maintained here. Adding or renaming a field on a slice hook
 * automatically flows into `ZuneloContextType` — the alternative (duplicating
 * every field's shape in this interface, again in the value literal below,
 * and a third time in `services/clients/index.ts`) is what let the three
 * copies drift out of memoized-object sync in the first place.
 */
type NavSlice = ReturnType<typeof useUiNavigationSlice>;
type JiraSlice = ReturnType<typeof useJiraSlice>;
type GitHubSlice = ReturnType<typeof useGitHubSlice>;
type SlackSlice = ReturnType<typeof useSlackSlice>;
type UnattendedInboxSlice = ReturnType<typeof useUnattendedInboxSlice>;
type ConversationsSlice = ReturnType<typeof useConversationsSlice>;
type AgentsSlice = ReturnType<typeof useAgentsSlice>;
type WorkspaceToolsSlice = ReturnType<typeof useWorkspaceToolsSlice>;
type NotificationsSlice = ReturnType<typeof useNotificationsSlice>;

interface ZuneloContextOwnFields {
  // Async States
  isLoading: boolean;
  loadingSources: Record<string, boolean>;
  errors: Record<string, string | null>;
  retry: (source?: string) => Promise<void>;
  refreshAll: () => Promise<void>;

  // Data
  items: AttentionItem[];
  currentUser?: CurrentUserIdentity;
  workContextIndex: ReadonlyMap<string, AttentionItemWorkContextMembership>;

  // Helpers
  getItemById: (id: string | null) => AttentionItem | undefined;
  getAgentByName: (name: string | null) => AgentItem | undefined;
  getJiraItemByKey: (key: string) => AttentionItem | undefined;
  getGitHubItemsByTicket: (ticketKey?: string) => AttentionItem[];
  getHoldInfo: (ticketKey?: string) => HoldInfo | undefined;
  getCrossReferences: (item: AttentionItem) => {
    siblings: Array<{
      key: string;
      title: string;
      age: string;
      tag: string;
      tone: string;
      source: SourceType;
      id: string;
    }>;
    externalSignals: SentryOrExtSignal[];
    holdInfo?: HoldInfo;
    suggestion?: StateSuggestion;
  };

  // AI Assistant attachments (shared across workspaces and Radar)
  aiAttachments: RadarAiAttachment[];
  sendToAssistant: (thread: WorkThread, attachmentId?: string) => void;
  removeAiAttachment: (id: string) => void;

  // Real-time source synchronization state
}

export type ZuneloContextType = NavSlice &
  JiraSlice &
  GitHubSlice &
  SlackSlice &
  UnattendedInboxSlice &
  ConversationsSlice &
  AgentsSlice &
  WorkspaceToolsSlice &
  NotificationsSlice &
  ZuneloContextOwnFields;

export const ZuneloContext = createContext<ZuneloContextType | undefined>(undefined);

export const ZuneloProvider: React.FC<{
  children: ReactNode;
  currentUser?: CurrentUserIdentity;
}> = ({ children, currentUser }) => {
  // Loading & Error States
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadingSources, setLoadingSources] = useState<Record<string, boolean>>({
    jira: true,
    slack: true,
    github: true,
    agents: true,
    gmail: false,
    workspace: true,
  });
  const [errors, setErrors] = useState<Record<string, string | null>>({
    jira: null,
    slack: null,
    unattendedInbox: null,
    github: null,
    agents: null,
    gmail: null,
    workspace: null,
    global: null,
  });

  // Derived Unified View Items
  const [items, setItems] = useState<AttentionItem[]>([]);
  const itemsRef = useRef<AttentionItem[]>([]);
  itemsRef.current = items;

  // Cross-reference / telemetry lookup tables
  const [holdData, setHoldData] = useState<Record<string, HoldInfo>>({});
  const [suggestData, setSuggestData] = useState<Record<string, StateSuggestion>>({});
  const [extSignals, setExtSignals] = useState<Record<string, SentryOrExtSignal[]>>({});
  const [prTalkMap, setPrTalkMap] = useState<PrTalkMap>({});
  const [aiAttachments, setAiAttachments] = useState<RadarAiAttachment[]>([]);

  const dataEpochRef = useRef(0);
  const mountEpochRef = useRef(0);
  const mountedRef = useRef(false);
  const beginWrite = useCallback(() => {
    dataEpochRef.current += 1;
  }, []);

  const hydrateSlackThreadCallbackRef = useRef<(id: string) => Promise<void>>(
    async () => undefined,
  );

  // UI Navigation Slice
  const nav = useUiNavigationSlice({
    onExpandItem: (id: string) => {
      void hydrateSlackThreadCallbackRef.current(id);
    },
  });

  // Domain Slices
  const retryRef = useRef<(source?: string) => Promise<void>>(async () => undefined);
  const retryBridge = useCallback(async (source?: string) => {
    await retryRef.current(source);
  }, []);

  const jira = useJiraSlice({
    setItems,
    beginWrite,
    retry: retryBridge,
    setErrors,
  });

  const github = useGitHubSlice({
    currentUser,
    itemsRef,
    setItems,
    beginWrite,
    retry: retryBridge,
    setErrors,
  });

  const slack = useSlackSlice({
    currentUser,
    itemsRef,
    setItems,
    setErrors,
    beginWrite,
    retry: retryBridge,
  });
  hydrateSlackThreadCallbackRef.current = slack.hydrateSlackThread;

  const unattended = useUnattendedInboxSlice({
    setErrors,
  });

  const conversationsSlice = useConversationsSlice({
    itemsRef,
    selectItem: nav.selectItem,
  });

  const agentsSlice = useAgentsSlice({
    beginWrite,
    retry: retryBridge,
  });

  const tools = useWorkspaceToolsSlice({
    setItems,
    beginWrite,
    retry: retryBridge,
    setIsDailyConfirmOpen: nav.setIsDailyConfirmOpen,
    currentUser,
    setErrors,
  });
  const notifications = useNotificationsSlice(currentUser);

  // ==========================================
  // Client Data Hydration
  // ==========================================

  const { setRawJiraIssues } = jira;
  const { setRawPullRequests } = github;
  const { setRawSlackThreads } = slack;
  const { setRawAgentSessions, setAgents } = agentsSlice;
  const {
    setRawGmailMessages,
    setReminders,
    setNotes,
    setLlmWiki,
    setDailyGroups,
    setDailySent,
    setPreviewOverride,
    setWorkContextGraph,
  } = tools;

  const loadAllData = useCallback(async () => {
    const epoch = dataEpochRef.current;
    const mountEpoch = mountEpochRef.current;
    const isMounted = () => mountEpochRef.current === mountEpoch;
    try {
      await runIncrementalBootstrap({
        currentUser,
        itemsRef,
        setItems,
        setRawJiraIssues,
        setRawPullRequests,
        setRawSlackThreads,
        setRawAgentSessions,
        setAgents,
        setRawGmailMessages,
        setReminders,
        setNotes,
        setLlmWiki,
        setDailyGroups,
        setDailySent,
        setHoldData,
        setSuggestData,
        setExtSignals,
        setPrTalkMap,
        setPreviewOverride,
        setWorkContextGraph,
        setErrors,
        setLoadingSources,
        setIsLoading,
        isMounted,
        dataEpoch: epoch,
        getDataEpoch: () => dataEpochRef.current,
      });
    } catch (err: unknown) {
      if (!isMounted()) return;
      setErrors((prev) => ({
        ...prev,
        global: toErrorMessage(err, 'Error de conexión general.'),
      }));
      setIsLoading(false);
    }
  }, [
    currentUser,
    setRawJiraIssues,
    setRawPullRequests,
    setRawSlackThreads,
    setRawAgentSessions,
    setAgents,
    setRawGmailMessages,
    setReminders,
    setNotes,
    setLlmWiki,
    setDailyGroups,
    setDailySent,
    setPreviewOverride,
    setWorkContextGraph,
  ]);

  useEffect(() => {
    const epoch = ++mountEpochRef.current;
    mountedRef.current = true;
    void loadAllData();
    return () => {
      if (mountEpochRef.current === epoch) {
        mountEpochRef.current += 1;
      }
      mountedRef.current = false;
    };
  }, [loadAllData]);

  const retry = useCallback(
    async (source?: string) => {
      if (!mountedRef.current) return;
      if (!source || source === 'global') {
        await loadAllData();
        return;
      }

      setLoadingSources((prev) => ({ ...prev, [source]: true }));
      setErrors((prev) => ({ ...prev, [source]: null }));

      try {
        await reloadAttentionSource(source, {
          currentUser,
          setItems,
          setRawJiraIssues,
          setRawPullRequests,
          setRawSlackThreads,
          setRawAgentSessions,
          setAgents,
          setRawGmailMessages,
          setReminders: tools.setReminders,
          setNotes: tools.setNotes,
          setDailyGroups: tools.setDailyGroups,
          setLlmWiki: tools.setLlmWiki,
          setWorkContextGraph: tools.setWorkContextGraph,
        });
      } catch (err: unknown) {
        if (mountedRef.current) {
          const message = sourceLoadErrorMessage(source, err);
          setErrors((prev) => ({ ...prev, [source]: message }));
        }
      } finally {
        if (mountedRef.current) {
          setLoadingSources((prev) => ({ ...prev, [source]: false }));
        }
      }
    },
    [
      currentUser,
      loadAllData,
      setRawJiraIssues,
      setRawPullRequests,
      setRawSlackThreads,
      setRawAgentSessions,
      setAgents,
      setRawGmailMessages,
      tools.setReminders,
      tools.setNotes,
      tools.setDailyGroups,
      tools.setLlmWiki,
      tools.setWorkContextGraph,
    ],
  );

  retryRef.current = retry;
  useSourceSyncIndex(async (source) => retryRef.current(source));

  // Item Helpers
  const getItemById = useCallback(
    (id: string | null) => {
      if (!id) return undefined;
      return items.find((item) => item.id === id);
    },
    [items],
  );

  const getAgentByName = useCallback(
    (name: string | null) => {
      if (!name) return undefined;
      return agentsSlice.agents.find((agent) => agent.name === name);
    },
    [agentsSlice.agents],
  );

  const getJiraItemByKey = useCallback(
    (key: string) => {
      return items.find((item) => item.source === 'jira' && item.ticket === key);
    },
    [items],
  );

  const getGitHubItemsByTicket = useCallback(
    (ticketKey?: string) => {
      return getGitHubItemsByTicketHelper(items, ticketKey);
    },
    [items],
  );

  const getHoldInfo = useCallback(
    (ticketKey?: string) => {
      return ticketKey ? holdData[ticketKey] : undefined;
    },
    [holdData],
  );

  // Precomputed per items-array-identity: getCrossReferences must be called with the
  // exact object currently held in `items` for a given id (e.g. via getItemById), or
  // with an item absent from `items` — see buildSiblingKeysMap's doc comment.
  const siblingKeysMap = useMemo(() => buildSiblingKeysMap(items), [items]);

  const getCrossReferences = useCallback(
    (item: AttentionItem) => {
      const targetKeys = siblingKeysMap.get(item.id) ?? attentionSiblingKeys(item);

      const siblings: Array<{
        key: string;
        title: string;
        age: string;
        tag: string;
        tone: string;
        source: SourceType;
        id: string;
      }> = [];

      items.forEach((x) => {
        if (x.id === item.id) return;
        const xKeys = siblingKeysMap.get(x.id) ?? attentionSiblingKeys(x);
        const hasShared = xKeys.some((k: string) => targetKeys.includes(k));
        if (hasShared) {
          siblings.push({
            key: x.source === 'jira' ? x.ticket || x.primary : x.primary,
            title: x.title,
            age: x.age,
            tag: x.source === 'jira' ? x.workflow || '' : x.source,
            tone: MetricsService.getAttentionBadgeTone(x.st),
            source: x.source,
            id: x.id,
          });
        }
      });

      const prTalks = prTalkMap[item.primary] || [];
      prTalks.forEach((pt) => {
        siblings.push({
          key: pt.ch,
          title: pt.text,
          age: pt.when,
          tag: 'mención en Slack',
          tone: 'accent',
          source: 'slack',
          id: pt.id,
        });
      });

      return {
        siblings,
        externalSignals: item.ticket ? extSignals[item.ticket] || [] : [],
        holdInfo: item.ticket ? holdData[item.ticket] : undefined,
        suggestion: item.ticket ? suggestData[item.ticket] : undefined,
      };
    },
    [items, prTalkMap, extSignals, holdData, suggestData, siblingKeysMap],
  );

  const { setView: navigateToRadarView } = nav;
  const sendToAssistant = useCallback(
    (thread: WorkThread, attachmentId?: string) => {
      const attachment = toRadarAiAttachment(thread);
      if (attachmentId && attachmentId !== attachment.id) {
        attachment.id = attachmentId;
      }
      setAiAttachments((prev) => upsertRadarAiAttachment(prev, attachment));
      navigateToRadarView('radar');
    },
    [navigateToRadarView],
  );

  const removeAiAttachment = useCallback((id: string) => {
    setAiAttachments((prev) => removeRadarAiAttachment(prev, id));
  }, []);

  // Shared work-context membership index, built ONCE per relevant state
  // change instead of once per mounted card. `useWorkContextMembership`
  // (and the same-family `workContextMembershipForItem` /
  // `itemWorkContextSnapshot` lookups) previously re-ran the full O(N)
  // clustering pass for every card on screen — O(N^2) as card count grows.
  const workContextIndex = useMemo(
    () =>
      indexAttentionItemWorkContexts(items, agentsSlice.agents, {
        extraRelations: tools.workContextGraph.relations,
        manualHubs: tools.workContextGraph.manualHubs,
        notes: tools.notes,
        reminders: tools.reminders,
      }),
    [items, agentsSlice.agents, tools.workContextGraph, tools.notes, tools.reminders],
  );

  // Every slice hook memoizes its own returned object (see
  // `context/slices/*`), so spreading them here instead of hand-listing
  // every field keeps this memo's dependency array meaningful: a slice's
  // object identity only changes when that slice's own state changes.
  const value: ZuneloContextType = useMemo(
    () => ({
      ...nav,
      ...jira,
      ...github,
      ...slack,
      ...unattended,
      ...conversationsSlice,
      ...agentsSlice,
      ...tools,
      ...notifications,

      // Async Data & Errors
      isLoading,
      loadingSources,
      errors,
      retry,
      refreshAll: loadAllData,

      // Derived Unified Items
      items,
      currentUser,
      workContextIndex,

      // Helpers
      getItemById,
      getAgentByName,
      getJiraItemByKey,
      getGitHubItemsByTicket,
      getHoldInfo,
      getCrossReferences,

      // AI Assistant attachments
      aiAttachments,
      sendToAssistant,
      removeAiAttachment,

      // Source synchronization
    }),
    [
      nav,
      jira,
      github,
      slack,
      unattended,
      conversationsSlice,
      agentsSlice,
      tools,
      notifications,
      isLoading,
      loadingSources,
      errors,
      retry,
      loadAllData,
      items,
      currentUser,
      workContextIndex,
      getItemById,
      getAgentByName,
      getJiraItemByKey,
      getGitHubItemsByTicket,
      getHoldInfo,
      getCrossReferences,
      aiAttachments,
      sendToAssistant,
      removeAiAttachment,
    ],
  );

  return <ZuneloContext.Provider value={value}>{children}</ZuneloContext.Provider>;
};

export const useZunelo = (): ZuneloContextType => {
  const context = useContext(ZuneloContext);
  if (!context) {
    throw new Error('useZunelo must be used within a ZuneloProvider');
  }
  return context;
};
